import type { ComputeRequest, ComputeSuccess, WorkerResponse } from './protocol';

/** {@link ComputeClient} が必要とする Worker の最小のインターフェース（テストで差し替えられる）。 */
export interface WorkerLike {
  postMessage(message: ComputeRequest): void;
  addEventListener(type: 'message', listener: (event: MessageEvent<WorkerResponse>) => void): void;
  addEventListener(type: 'error', listener: (event: ErrorEvent) => void): void;
  terminate(): void;
}

/** 計算の結果。`stale` は、より新しい要求が出されたため破棄されたことを表す。 */
export type ComputeOutcome =
  | { readonly status: 'ok'; readonly value: ComputeSuccess }
  | { readonly status: 'error'; readonly message: string }
  | { readonly status: 'stale' };

type Pending = (outcome: ComputeOutcome) => void;

/**
 * Worker との型付きメッセージのやり取り。
 * 新しい要求を出すと、未完了の古い要求は `stale` として解決され、古い結果は捨てられる
 * （編集中の連続更新に対応するため）。
 */
export class ComputeClient {
  private nextId = 1;
  private latestId = 0;
  private readonly pending = new Map<number, Pending>();

  constructor(private readonly worker: WorkerLike) {
    worker.addEventListener('message', (event) => {
      this.receive(event.data);
    });
    worker.addEventListener('error', (event) => {
      this.failAll(event.message || 'Worker でエラーが発生しました');
    });
  }

  /** 要求を送る。`requestId` はクライアントが採番する。 */
  compute(request: Omit<ComputeRequest, 'type' | 'requestId'>): Promise<ComputeOutcome> {
    const requestId = this.nextId;
    this.nextId += 1;
    this.latestId = requestId;
    for (const [id, resolve] of this.pending) {
      if (id < requestId) {
        this.pending.delete(id);
        resolve({ status: 'stale' });
      }
    }
    return new Promise((resolve) => {
      this.pending.set(requestId, resolve);
      this.worker.postMessage({ type: 'compute', requestId, ...request });
    });
  }

  /** Worker を終了し、未完了の要求を `stale` にする。 */
  dispose(): void {
    this.worker.terminate();
    for (const resolve of this.pending.values()) resolve({ status: 'stale' });
    this.pending.clear();
  }

  private receive(response: WorkerResponse): void {
    const resolve = this.pending.get(response.requestId);
    if (resolve === undefined || response.requestId !== this.latestId) return; // 古い結果は捨てる
    this.pending.delete(response.requestId);
    resolve(
      response.type === 'result'
        ? { status: 'ok', value: response }
        : { status: 'error', message: response.message },
    );
  }

  private failAll(message: string): void {
    for (const resolve of this.pending.values()) resolve({ status: 'error', message });
    this.pending.clear();
  }
}

/** ブラウザの Web Worker で {@link ComputeClient} を作る。 */
export function createComputeClient(): ComputeClient {
  const worker = new Worker(new URL('./compute.worker.ts', import.meta.url), { type: 'module' });
  return new ComputeClient(worker);
}
